'use client';

import Link from 'next/link';
import { PortableText, type PortableTextComponents } from 'next-sanity';
import type { PortableTextBlock } from 'sanity';
import type { IPageLink } from '@/sanity/globals/CTA';
import { resolvePageLink } from '@/sanity/globals/CTA';
import { ArrowRightIcon, InformationIcon } from './Icons';

const noteComponents: PortableTextComponents = {
  block: { normal: ({ children }) => <p className="mb-0">{children}</p> },
  marks: {
    strong: ({ children }) => <strong className="font-medium text-black">{children}</strong>,
  },
};

export default function NoteBar({
  content,
  linkLabel,
  link,
  language,
}: {
  content?: PortableTextBlock[];
  linkLabel?: string;
  link?: IPageLink;
  language?: string;
}) {
  if (!content?.length && !linkLabel) return null;
  const href = link ? resolvePageLink(link, language) : null;
  const linkNode = linkLabel && (
    <span data-ui="note.link" className="inline-flex items-center gap-[0.75rem] font-geist text-[2rem] font-medium leading-[3rem] text-[#228e71]">
      {linkLabel}
      <ArrowRightIcon data-ui="note.arrow" className="h-[2rem] w-[2rem]" />
    </span>
  );

  return (
    <div
      data-ui="rules-v3.note"
      className="flex w-full flex-col gap-[1.5rem] rounded-[0.25rem] bg-[#f2f4f3] px-[2rem] py-[2rem] lg:flex-row lg:items-center lg:justify-between lg:px-[2.5rem]"
    >
      <div className="flex items-start gap-[1.5rem] lg:items-center">
        <InformationIcon data-ui="note.icon" className="mt-[0.5rem] h-[2.5rem] w-[2.5rem] shrink-0 text-[#228e71] lg:mt-0" />
        <div data-ui="note.text" className="font-geist text-[2rem] leading-[3rem] text-black/[0.64] lg:w-[76.625rem]">
          {content && <PortableText value={content} components={noteComponents} />}
        </div>
      </div>
      {linkNode && (
        <div className="pl-[4rem] lg:pl-0">
          {href ? (
            <Link href={href} target={link?.openInNewTab ? '_blank' : undefined}>
              {linkNode}
            </Link>
          ) : (
            linkNode
          )}
        </div>
      )}
    </div>
  );
}
